import {
  DeleteForever,
  Grading,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  PostAdd,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip
} from "@mui/material";
import React from "react";
import { IAssignment, IManagedUser } from "../../types";

export interface ManagedUserRowDelegate {
  didClickAddInstrument: (userUid: string) => void;
  didClickSeeResults: (assignmentId: number) => void;
  didClickDeleteAssignment: (assignmentId: number, userUid: string) => void;
}

interface State {
  open: boolean;
  anchorUser: HTMLElement | null;
  anchorAssign: HTMLElement | null;
  selectedAssignId: number | null;
}

interface Props {
  user: IManagedUser;
  delegate: ManagedUserRowDelegate;
}

class InvitedUsersRow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      anchorUser: null,
      anchorAssign: null,
      selectedAssignId: null,
    };
  }

  toggleOpen = () => {
    this.setState((s) => ({ open: !s.open }));
  };

  openUserMenu = (e: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorUser: e.currentTarget });
  };
  closeUserMenu = () => {
    this.setState({ anchorUser: null });
  };
  handleAddInstrument = () => {
    this.props.delegate.didClickAddInstrument(this.props.user.uid);
    this.closeUserMenu();
  };

  openAssignMenu = (e: React.MouseEvent<HTMLElement>, assignmentId: number) => {
    this.setState({
      anchorAssign: e.currentTarget,
      selectedAssignId: assignmentId,
    });
  };
  closeAssignMenu = () => {
    this.setState({ anchorAssign: null, selectedAssignId: null });
  };
  handleSeeResults = () => {
    this.props.delegate.didClickSeeResults(this.state.selectedAssignId!);
    this.closeAssignMenu();
  };
  handleDeleteAssignment = () => {
    this.props.delegate.didClickDeleteAssignment(
      this.state.selectedAssignId!,
      this.props.user.uid
    );
    this.closeAssignMenu();
  };

  renderAssignmentStatusChip = (assignment: IAssignment) => {
    if (assignment.finishedAt) {
      return (
        <Chip
          label="Finalizado"
          color="success"
          size="small"
        />
      );
    }
    if (assignment.startedAt) {
      return (
        <Chip
          label="Iniciado"
          color="info"
          size="small"
        />
      );
    }
    return (
      <Chip
        label="Não iniciado"
        color="default"
        size="small"
      />
    );
  };

  render() {
    const { user } = this.props;
    const { open, anchorUser, anchorAssign, selectedAssignId } = this.state;
    const identity = user.identity;
    const name = identity
      ? `${identity.firstName} ${identity.lastName}`
      : "Anônimo";
    const email = identity?.email ?? "-";
    const invited = identity?.isInvitation ?? false;
    const instrumentCount = user.assignments.length;
    const userMenuOpen = Boolean(anchorUser);
    const assignMenuOpen = Boolean(anchorAssign);

    return (
      <>
        <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
          <TableCell>
            <IconButton size="small" onClick={this.toggleOpen}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>{name}</TableCell>
          <TableCell>{email}</TableCell>
          <TableCell align="center">{instrumentCount}</TableCell>
          <TableCell align="center">
            {invited ? <Chip label="Em aberto" color="warning" variant="outlined" size="small"/> : <Chip label="Aceito" color="success" variant="outlined" size="small"/>}
          </TableCell>
          <TableCell align="right">
            <IconButton 
              aria-label="actions"
              size="medium"
              sx={{
                backgroundColor: "grey.200",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "grey.300",
                },
              }} 
              onClick={this.openUserMenu}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorUser}
              open={userMenuOpen}
              onClose={this.closeUserMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={this.handleAddInstrument}>
                <PostAdd fontSize="small" sx={{ mr: 1 }} />
                Atribuir Aplicação
              </MenuItem>
            </Menu>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Aplicações
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Aplicação</TableCell>
                      <TableCell>Data Limite</TableCell>
                      <TableCell>Instrumento</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.assignments.map((a) => {
                      const finished = Boolean(a.finishedAt);
                      return (
                        <TableRow key={a.assignmentId}>
                          <TableCell>{a.applicationName}</TableCell>
                      <TableCell>
                        {a.dueDate.toLocaleDateString()}{" "}
                        {a.dueDate.toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{a.title}</TableCell>
                      <TableCell align="center">
                        {this.renderAssignmentStatusChip(a)}
                      </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                this.openAssignMenu(e, a.assignmentId)
                              }
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                            <Menu
                              anchorEl={anchorAssign}
                              open={
                                assignMenuOpen &&
                                selectedAssignId === a.assignmentId
                              }
                              onClose={this.closeAssignMenu}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              {finished ? (
                                <MenuItem onClick={this.handleSeeResults}>
                                  <Grading fontSize="small" sx={{ mr: 1 }} />
                                  Ver Resultados
                                </MenuItem>
                              ) : (
                                <Tooltip
                                  title="Resultado disponível após finalização"
                                  placement="left"
                                >
                                  <div>
                                    <MenuItem disabled>
                                      <Grading
                                        fontSize="small"
                                        sx={{ mr: 1 }}
                                      />
                                      Ver Resultados
                                    </MenuItem>
                                  </div>
                                </Tooltip>
                              )}

                              {!finished ? (
                                <MenuItem onClick={this.handleDeleteAssignment}>
                                  <DeleteForever
                                    fontSize="small"
                                    sx={{ mr: 1 }}
                                  />
                                  Remover Aplicação
                                </MenuItem>
                              ) : (
                                <Tooltip
                                  title="Não é possível remover após finalização"
                                  placement="left"
                                >
                                  <div>
                                    <MenuItem disabled>
                                      <DeleteForever
                                        fontSize="small"
                                        sx={{ mr: 1 }}
                                      />
                                      Remover Aplicação
                                    </MenuItem>
                                  </div>
                                </Tooltip>
                              )}
                            </Menu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }
}

export default InvitedUsersRow;
